// src/pages/Enseignants/Events/UploadEventsModal.jsx
import React, { useState } from "react";
import { FaTimes, FaUpload, FaDownload, FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { uploadEventsFile } from "../../../services/eventServices";
import "react-toastify/dist/ReactToastify.css";
import Button from "../../../components/Button/Button";

const FIELDS = [
  {
    key: "id",
    label: "id",
    required: "Optionnel",
    type: "number",
    note: "Laisser vide pour créer; renseigner pour mettre à jour",
  },
  {
    key: "titre",
    label: "titre",
    required: "Requis*",
    type: "string",
    note: "Nom de l’événement",
  },
  {
    key: "description",
    label: "description",
    required: "Optionnel",
    type: "string",
    note: "",
  },
  {
    key: "start",
    label: "start",
    required: "Requis*",
    type: "date",
    note: "Date Excel (cellule Date) — évitez du texte",
  },
  {
    key: "end",
    label: "end",
    required: "Optionnel",
    type: "date",
    note: "Date Excel (cellule Date)",
  },
  {
    key: "type_event",
    label: "type_event",
    required: "Requis*",
    type: "string",
    note: "Ex: Compétition / Animation…",
  },
  {
    key: "level_event",
    label: "level_event",
    required: "Requis*",
    type: "string",
    note: "Club / Départemental / …",
  },
  {
    key: "cate_event",
    label: "cate_event",
    required: "Optionnel",
    type: "string",
    note: "Liste séparée par virgules (ex: Poussin(e), Benjamin(e))",
  },
  {
    key: "lieu_event",
    label: "lieu_event",
    required: "Optionnel",
    type: "string",
    note: "Ville / salle",
  },
  {
    key: "horaire_event",
    label: "horaire_event",
    required: "Optionnel",
    type: "string",
    note: "Texte ou HTML (ex: Pesée 8h30…)",
  },
  {
    key: "photo_url",
    label: "photo_url",
    required: "Optionnel",
    type: "url",
    note: "",
  },
  {
    key: "agenda_url",
    label: "agenda_url",
    required: "Optionnel",
    type: "url",
    note: "",
  },
  {
    key: "is_active",
    label: "is_active",
    required: "Optionnel",
    type: "string",
    note: `Mettre "true" ou "false" (en minuscules)`,
  },
];

const SAMPLE_ROW = {
  id: "",
  titre: "Test Compétition",
  description: "Open départemental",
  start: new Date("2025-09-01"),
  end: new Date("2025-09-01"),
  type_event: "Compétition",
  level_event: "Départemental",
  cate_event: "Poussin(e), Benjamin(e)",
  lieu_event: "Bordeaux",
  horaire_event: "Pesée 8h30 — Début 9h30",
  photo_url: "",
  agenda_url: "",
  is_active: "true",
};

const UploadEventsModal = ({ isOpen, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!isOpen) return null;

  const safeClose = () => {
    setFile(null);
    onClose?.();
  };

  const onFileChange = (e) => setFile(e.target.files?.[0] || null);

  const handleSubmit = async (e) => {
    e?.preventDefault(); // supporte onSubmit et onClick direct
    if (!file) {
      toast.error("Veuillez sélectionner un fichier (.xlsx, .xls, .csv).");
      return;
    }
    setBusy(true);
    try {
      await uploadEventsFile(file); // <- l’upload se fait ici (dans la modale)
      toast.success("Calendrier importé avec succès !");
      safeClose();
      onUploaded?.(); // <- juste pour refresh côté parent
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'import du fichier.");
    } finally {
      setBusy(false);
    }
  };

  // Téléchargement modèle : tente XLSX (lib 'xlsx'), sinon CSV
  const handleDownloadTemplate = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    try {
      const XLSX = await import("xlsx"); // `npm i xlsx`
      const rows = [SAMPLE_ROW];
      const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "modele_evenements");
      ws["!cols"] = FIELDS.map(() => ({ wch: 18 }));

      const wbout = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modele_evenements.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Modèle XLSX téléchargé.");
    } catch (err) {
      console.error(err);
      const headers = FIELDS.map((f) => f.key).join(",");
      const csvRow = [
        SAMPLE_ROW.id,
        SAMPLE_ROW.titre,
        SAMPLE_ROW.description,
        "2025-09-01",
        "2025-09-01",
        SAMPLE_ROW.type_event,
        SAMPLE_ROW.level_event,
        `"${SAMPLE_ROW.cate_event}"`,
        SAMPLE_ROW.lieu_event,
        `"${String(SAMPLE_ROW.horaire_event).replace(/"/g, '""')}"`,
        SAMPLE_ROW.photo_url,
        SAMPLE_ROW.agenda_url,
        SAMPLE_ROW.is_active,
      ].join(",");
      const csv = `\ufeff${headers}\n${csvRow}\n`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modele_evenements.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.info("Modèle CSV téléchargé (préférez XLSX si possible).");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      {/* Conteneur 85vh + header sticky */}
      <div
        className="relative w-full max-w-5xl h-[85vh] max-h-[85vh] overflow-y-auto rounded-2xl shadow-xl ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-white/90 dark:bg-slate-900/80"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-7 py-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <h3 className="text-base sm:text-lg font-semibold">
            Importer un calendrier d’événements
          </h3>
          <button
            type="button"
            onClick={safeClose}
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps */}
        <div className="grid md:grid-cols-2 gap-0">
          {/* Colonne gauche : Upload */}
          <div
            className="p-5 sm:p-7 space-y-5"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClickCapture={(e) => e.stopPropagation()}
            onMouseDownCapture={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <FaInfoCircle className="opacity-70" /> Instructions
              </div>
              <ul className="list-disc pl-5 text-sm text-slate-700 dark:text-slate-200 space-y-1">
                <li>
                  Formats acceptés : <strong>XLSX, XLS</strong> (recommandé) ou{" "}
                  <strong>CSV</strong>.
                </li>
                <li>
                  Pour <strong>start/end</strong>, utilisez une{" "}
                  <strong>cellule Date Excel</strong> (pas du texte).
                </li>
                <li>
                  <code>is_active</code> : mettez <code>"true"</code> ou{" "}
                  <code>"false"</code> en minuscules.
                </li>
                <li>
                  <code>cate_event</code> : séparez par virgules (ex:{" "}
                  <em>Poussin(e), Benjamin(e)</em>).
                </li>
              </ul>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  text="Télécharger le modèle"
                  icon={FaDownload}
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleDownloadTemplate(e);
                  }}
                  hoverLabel="Télécharger un fichier modèle (XLSX recommandé)"
                  className="!rounded-xl gap-2"
                />
              </div>
            </div>

            <form
              id="uploadEventsForm" // ⬅️ important
              onSubmit={handleSubmit}
              onClick={(e) => e.stopPropagation()}
              className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/60 p-4 space-y-4"
            >
              <label className="block text-sm font-medium mb-1">Fichier</label>
              <input
                id="eventsFileInput"
                name="eventsFile"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onFileChange}
                className="file-input file-input-bordered w-full max-w-md"
                aria-label="Fichier calendrier (xlsx, xls, csv)"
              />
              {file && (
                <div className="text-xs opacity-80">
                  Sélectionné : <strong>{file.name}</strong>
                </div>
              )}
              {/* ⬇️ SUPPRIMER le bloc de boutons ici */}
            </form>
          </div>

          {/* Colonne droite : Modèle attendu */}
          <div className="p-5 sm:p-7 bg-slate-50/60 dark:bg-slate-900/40 border-l border-slate-200 dark:border-slate-800">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/85 dark:bg-slate-900/70">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Modèle attendu (colonnes)
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[680px] w-full text-sm">
                  <thead className="bg-slate-50/90 dark:bg-slate-900/70">
                    <tr className="text-left text-slate-700 dark:text-slate-200">
                      <th className="px-3 py-2">Champ</th>
                      <th className="px-3 py-2">Obligation</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {FIELDS.map((f) => (
                      <tr
                        key={f.key}
                        className="bg-white/90 dark:bg-slate-900/40"
                      >
                        <td className="px-3 py-2 font-mono">{f.label}</td>
                        <td className="px-3 py-2">{f.required}</td>
                        <td className="px-3 py-2">{f.type}</td>
                        <td className="px-3 py-2">{f.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Exemple de ligne */}
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 text-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                  Exemple
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-[680px] w-full text-xs">
                    <thead>
                      <tr className="text-left">
                        {FIELDS.map((f) => (
                          <th
                            key={f.key}
                            className="px-2 py-1 font-mono bg-slate-50 dark:bg-slate-800/60"
                          >
                            {f.key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {FIELDS.map((f) => {
                          const v = SAMPLE_ROW[f.key];
                          const display =
                            v instanceof Date
                              ? v.toLocaleDateString("fr-FR")
                              : String(v ?? "");
                          return (
                            <td key={f.key} className="px-2 py-1">
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-[11px] opacity-75">
                  ⚠️ Préférez le modèle <strong>XLSX</strong> pour que les dates
                  soient de vraies cellules “Date”. Le CSV peut convertir les
                  dates en texte selon Excel.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Footer - Boutons */}
        {/* Footer global (hors colonne gauche) */}
        <div
          className="sticky bottom-0 z-10 px-5 sm:px-7 py-4 bg-white/90 dark:bg-slate-900/80 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            text={busy ? "Import..." : "Importer"}
            icon={FaUpload}
            onClick={(e) => {
              e?.stopPropagation();
              if (busy || !file) return;
              handleSubmit(); // pas besoin d'event, handleSubmit gère l'absence de e
            }}
            hoverLabel="Importer le fichier sélectionné"
            className={`!rounded-xl gap-2 ${
              busy || !file ? "opacity-50 pointer-events-none" : ""
            }`}
          />

          <Button
            text="Fermer"
            icon={FaTimes}
            onClick={(e) => {
              e?.stopPropagation();
              onClose?.();
            }}
            hoverLabel="Fermer la fenêtre"
            className="!rounded-xl gap-2"
          />
        </div>
      </div>
    </div>
  );
};

export default UploadEventsModal;
